import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { AdminDataProvider, useAdminData } from './contexts/AdminDataContext';
import { AdminShell } from './components/admin/AdminShell';
import { PublicSite } from './pages/PublicSite';
import { AdminLogin } from './pages/admin/AdminLogin';
import { Overview } from './pages/admin/Overview';
import { Today } from './pages/admin/Today';
import { Enquiries } from './pages/admin/Enquiries';
import { Quotes } from './pages/admin/Quotes';
import { QuoteNew } from './pages/admin/QuoteNew';
import { QuoteDetail } from './pages/admin/QuoteDetail';
import { Cases } from './pages/admin/Cases';
import { CaseNew } from './pages/admin/CaseNew';
import { CaseDetail } from './pages/admin/CaseDetail';
import { Invoices } from './pages/admin/Invoices';
import { InvoiceDetail } from './pages/admin/InvoiceDetail';
import { Customers } from './pages/admin/Customers';
import { CustomerDetail } from './pages/admin/CustomerDetail';
import { Settings } from './pages/admin/Settings';
import { Calendar } from './pages/admin/Calendar';
import { CasesGallery } from './pages/admin/CasesGallery';
import { company } from './data/site';
import { applyBrandIcon } from './utils/favicon';
import { initAnalytics } from './utils/analytics';
import { adminThemeClass, readAdminTheme } from './hooks/useAdminTheme';
function RequireAdmin({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    loggedIn,
    authLoading
  } = useAdminData();
  const location = useLocation();
  if (authLoading) {
    return <div className={`${adminThemeClass(readAdminTheme())} flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-canvas`} data-id="element-0" data-mp="38kqcs5pvg9xu">
        <img src={company.logoMark} alt="" className="h-14 w-14 object-contain" data-id="element-1" data-mp="3dirtwl3qlz6o" />
        <p className="flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-2" data-mp="nv0wrpdfmi3k">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-3" data-mp="stkn01z26g1q" />
          Henter …
        </p>
      </div>;
  }

  // Den ønskede adresse huskes, så man efter login lander på fx sagen fra en notifikation.
  if (!loggedIn) {
    return <Navigate to="/admin/login" replace state={{
      from: `${location.pathname}${location.search}`
    }} data-id="element-4" data-mp="3uevuw836co7b" />;
  }
  return <>{children}</>;
}
export function App() {
  useEffect(() => {
    applyBrandIcon(company.logoMark, 'Lykkeværk');
    void initAnalytics();
  }, []);
  return <AdminDataProvider data-id="element-5" data-mp="3glwbvw46obv0">
      <BrowserRouter data-id="element-6" data-mp="3gja2rs1n9oao">
        <Routes data-id="element-7" data-mp="3bmjq7ep1tau0">
          <Route path="/" element={<PublicSite data-id="element-9" data-mp="3bnmedeuzkz68" />} data-id="element-8" data-mp="3bn2ys4st6mvd" />
          <Route path="/admin/login" element={<AdminLogin data-id="element-11" data-mp="3qbpp11jz4z1d" />} data-id="element-10" data-mp="3bk720jsp648i" />
          <Route path="/admin" element={<RequireAdmin data-id="element-13" data-mp="3ljxvci5iud70">
                <AdminShell data-id="element-14" data-mp="3ljphamxnx1u8" />
              </RequireAdmin>} data-id="element-12" data-mp="3qbzg6sgr9c8n">
            <Route index element={<Overview data-id="element-16" data-mp="2xtlolam0imi0" />} data-id="element-15" data-mp="2xu3i3zhaimm8" />
            <Route path="i-dag" element={<Today data-id="element-18" data-mp="2sw1ztgrourbt" />} data-id="element-17" data-mp="2xssebznuhev4" />
            <Route path="henvendelser" element={<Enquiries data-id="element-20" data-mp="2svjyi5q65mx1" />} data-id="element-19" data-mp="2suqxzmhsztnc" />
            <Route path="tilbud" element={<Quotes data-id="element-22" data-mp="18dhp3w5i2m2b" />} data-id="element-21" data-mp="18drjrx1hphec" />
            <Route path="tilbud/ny" element={<QuoteNew data-id="element-24" data-mp="18by9m7ro4f1l" />} data-id="element-23" data-mp="18c6p87f6dygh" />
            <Route path="tilbud/:quoteId" element={<QuoteDetail data-id="element-26" data-mp="1d62gys4uip37" />} data-id="element-25" data-mp="1d4ztyvan96sk" />
            <Route path="sager" element={<Cases data-id="element-28" data-mp="yg3mazci1vsh" />} data-id="element-27" data-mp="1d6ayixbwyikq" />
            <Route path="sager/ny" element={<CaseNew data-id="element-30" data-mp="ydija70gt9t5" />} data-id="element-29" data-mp="ygwl9e57vh02" />
            <Route path="sager/:caseId" element={<CaseDetail data-id="element-32" data-mp="13fpxl38rp1yn" />} data-id="element-31" data-mp="13eexpeig0ect" />
            <Route path="kalender" element={<Calendar data-id="element-34" data-mp="1r4inoj2pkflc" />} data-id="element-33" data-mp="13ey7g7xxvwfr" />
            <Route path="cases" element={<CasesGallery data-id="element-36" data-mp="1r4sj4m6iveyf" />} data-id="element-35" data-mp="1r5aktybyeldq" />
            <Route path="fakturaer" element={<Invoices data-id="element-38" data-mp="1w3lwmelt75yl" />} data-id="element-37" data-mp="1w3c2cetqecn3" />
            <Route path="fakturaer/:invoiceId" element={<InvoiceDetail data-id="element-40" data-mp="1i3kr2lulnacs" />} data-id="element-39" data-mp="1w2tzgzckd067" />
            <Route path="kunder" element={<Customers data-id="element-42" data-mp="1i3b3f4x11fa9" />} data-id="element-41" data-mp="1i2j6nqrp1bie" />
            <Route path="kunder/:customerId" element={<CustomerDetail data-id="element-44" data-mp="1m44s40iamy47" />} data-id="element-43" data-mp="1m56364zb7klx" />
            <Route path="indstillinger" element={<Settings data-id="element-46" data-mp="6onqcep1ywpl" />} data-id="element-45" data-mp="1m3cxarwit4ez" />
          </Route>
          <Route path="*" element={<Navigate to="/" replace data-id="element-48" data-mp="6qies2zj96yf" />} data-id="element-47" data-mp="6mujayaj2ah2" />
        </Routes>
      </BrowserRouter>
    </AdminDataProvider>;
}